import { CartLinesUpdateEvent } from '@shopify/events';

const routes = (window.Theme && window.Theme.routes) || {};
const ADD_URL = routes.cart_add_url || '/cart/add';
const CART_URL = routes.cart_url || '/cart';

/** Adds items to the cart and tells the Horizon cart (drawer, icon, cart page) to refresh. */
export async function amauveAddItems(source, items) {
  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    })
  );
  try {
    const res = await fetch(ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sections.join(',') }),
    }).then((r) => r.json());
    const cart = await fetch(CART_URL + '.json', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const total = items.reduce((s, i) => s + i.quantity, 0);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: total, sections: res.sections, source: 'amauve', didError: !!res.status },
    });
    if (res.status) throw new Error(res.description || res.message || 'Could not add to cart');
    return cart;
  } catch (err) {
    deferred.reject(err);
    throw err;
  }
}
window.amauveAddItems = amauveAddItems;

/** Opens the Horizon cart drawer, or goes to the cart page if the store uses a cart page. */
export function amauveShowCart() {
  const drawer = document.querySelector('#cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    if (!drawer.isOpen) setTimeout(() => drawer.open(), 150);
  } else {
    window.location.href = CART_URL;
  }
}

/* ---------- Price helpers (button total = product price + ticked add-ons) ---------- */
const parseCents = (s) => {
  const m = String(s || '').match(/\d[\d.,]*/);
  if (!m) return 0;
  const n = m[0].replace(/[.,](?=\d{3}(\D|$))/g, '').replace(',', '.');
  return Math.round(parseFloat(n) * 100) || 0;
};
const formatLike = (template, cents) => {
  const num = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return String(template).replace(/\d[\d.,]*/, num);
};
const addonsOf = (form) => Array.from(form.elements).filter((el) => el.matches && el.matches('input[data-am-addon]'));

function updateButtonTotal(form) {
  if (!form) return;
  const span = form.querySelector('[data-am-btnprice]');
  if (!span) return;
  if (!span.dataset.base) span.dataset.base = span.textContent.trim();
  let cents = parseCents(span.dataset.base);
  addonsOf(form).forEach((el) => {
    if (!el.checked) return;
    const priceEl = el.closest('.am-addon') && el.closest('.am-addon').querySelector('.am-addon__price');
    const first = priceEl && priceEl.firstChild ? priceEl.firstChild.textContent : '';
    cents += parseCents(first);
  });
  span.textContent = formatLike(span.dataset.base, cents);
}
const updateAllTotals = () => document.querySelectorAll('form[data-am-form]').forEach(updateButtonTotal);

/* ---------- Gallery arrows ---------- */
function galleryStep(button) {
  const stage = button.closest('.am-pdp__stage');
  const track = stage && stage.querySelector('[data-am-track]');
  if (!track) return;
  const slides = Array.from(track.children);
  if (slides.length < 2) return;
  const origin = slides[0].offsetLeft;
  const current = slides.reduce((best, s, i) => (Math.abs(s.offsetLeft - origin - track.scrollLeft) < Math.abs(slides[best].offsetLeft - origin - track.scrollLeft) ? i : best), 0);
  let next = current + Number(button.dataset.amGal);
  if (next >= slides.length) next = 0;
  if (next < 0) next = slides.length - 1;
  track.scrollTo({ left: slides[next].offsetLeft - origin, behavior: 'smooth' });
}

if (!window.__amauveProductInit) {
  window.__amauveProductInit = true;

  // Browsers can restore a ticked checkbox on reload/back; always start from the editor's default.
  const resetAddons = () => {
    document.querySelectorAll('input[data-am-addon]').forEach((el) => { el.checked = el.defaultChecked; });
    updateAllTotals();
  };
  resetAddons();
  window.addEventListener('pageshow', resetAddons);

  // Add to cart (with optional add-ons) without leaving the page.
  document.addEventListener('submit', async (e) => {
    const form = e.target && e.target.closest ? e.target.closest('form[data-am-form]') : null;
    if (!form || e.defaultPrevented || form.dataset.amAjax !== 'true') return;
    e.preventDefault();
    const fd = new FormData(form);
    const id = Number(fd.get('id'));
    if (!id) return;
    const items = [{ id, quantity: Math.max(1, Number(fd.get('quantity')) || 1) }];
    // form.elements includes add-on checkboxes linked with the form="" attribute.
    // (Don't use form.id here: the hidden input named "id" shadows it.)
    addonsOf(form).forEach((el) => {
      if (el.checked && el.value) items.push({ id: Number(el.value), quantity: 1 });
    });
    const btn = form.querySelector('[data-am-atc]');
    const err = form.querySelector('[data-am-error]');
    if (err) err.hidden = true;
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
    try {
      await amauveAddItems(form, items);
      if (btn) { btn.classList.add('is-added'); setTimeout(() => btn.classList.remove('is-added'), 1600); }
      amauveShowCart();
    } catch (ex) {
      if (err) { err.textContent = ex.message; err.hidden = false; }
    } finally {
      if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
    }
  });

  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!t || !t.closest) return;

    // Add-on ticked/unticked: update the button total.
    if (t.matches('input[data-am-addon]')) {
      updateButtonTotal(t.form);
      return;
    }

    // Variant picker: keep price, savings and button in sync.
    const sel = t.closest('select[data-am-variant]');
    if (!sel) return;
    const o = sel.selectedOptions[0];
    const root = document.getElementById(sel.dataset.root);
    if (!root || !o) return;
    root.querySelectorAll('[data-am-now]').forEach((n) => (n.textContent = o.dataset.price));
    root.querySelectorAll('[data-am-btnprice]').forEach((n) => { n.dataset.base = o.dataset.price; });
    root.querySelectorAll('[data-am-was]').forEach((n) => { n.textContent = o.dataset.compare || ''; n.hidden = !o.dataset.compare; });
    root.querySelectorAll('[data-am-save]').forEach((n) => { n.textContent = (n.dataset.label || '') + ' ' + (o.dataset.pct || '') + '%'; n.hidden = !o.dataset.pct; });
    const available = o.dataset.available === 'true';
    root.querySelectorAll('[data-am-atc]').forEach((b) => {
      b.disabled = !available;
      const tx = b.querySelector('[data-am-atc-text]');
      if (tx) tx.textContent = available ? b.dataset.label : b.dataset.soldout;
      const pr = b.querySelector('[data-am-btnprice-wrap]');
      if (pr) pr.hidden = !available;
    });
    updateButtonTotal(sel.form);
    const url = new URL(window.location.href);
    if (url.pathname.indexOf('/products/') !== -1) { url.searchParams.set('variant', o.value); history.replaceState(null, '', url); }
  });

  document.addEventListener('click', (e) => {
    if (!e.target || !e.target.closest) return;

    // Gallery arrows.
    const g = e.target.closest('[data-am-gal]');
    if (g) { galleryStep(g); return; }

    // Quantity +/- buttons.
    const b = e.target.closest('[data-am-qty]');
    if (!b) return;
    const input = b.parentElement.querySelector('input');
    const next = (Number(input.value) || 1) + Number(b.dataset.amQty);
    input.value = Math.max(1, next);
  });
}
